import type {
  FileNamePlaceholder,
  SftpFileConfig,
  SftpFileConfigWire,
  SftpFileType,
} from '../types/sftp.types';
import { SFTP_FILE_TYPES, SUBSIDIARY_CODE_PLACEHOLDER } from '../types/sftp.types';

/**
 * User-facing guidance per `errorCode` from `connections/test` and
 * `connections/{id}/validate`. Create's 400 carries the same message without
 * the code, which is why the form offers a pre-save test at all.
 */
const SFTP_ERROR_COPY: Record<string, string> = {
  AUTH_FAILED: 'The server rejected these credentials. Check the username and password or key.',
  SSH_BANNER_ERROR: "This host and port isn't an SFTP server. SFTP usually uses port 22.",
  CONNECTION_TIMEOUT:
    "The server didn't respond within 10 seconds. Check the host, the port, and that Lily's IPs are allowed through the firewall.",
  CONNECTION_REFUSED: 'The server refused the connection. Check that SFTP is running on this port.',
  HOST_NOT_FOUND: "Couldn't find this hostname.",
  DIRECTORY_NOT_FOUND: "The base folder doesn't exist on the server.",
  NOT_A_DIRECTORY: 'The base path is a file, not a folder.',
  INVALID_PRIVATE_KEY:
    "Couldn't read the private key. Paste the full key including its BEGIN and END lines (OpenSSH or PEM; PuTTY .ppk keys aren't supported), and check the passphrase if the key is encrypted.",
  MISSING_CREDENTIALS: 'Enter a username and a password or private key.',
  LILY_SFTP_DEFAULTS_MISSING: "Lily-managed SFTP isn't available in this environment.",
  MISSING_HOST_FOR_CLIENT_SFTP: 'Enter the server hostname.',
  MISSING_DEPENDENCY: 'Something is missing on our side. Contact support.',
};

/** Human copy for a connection-check failure; falls back to the server message
 *  (INVALID_CONFIG / INVALID_CREDENTIALS name the field themselves). */
export function describeConnectionCheckError(
  errorCode: string | null | undefined,
  message: string,
): string {
  return (errorCode ? SFTP_ERROR_COPY[errorCode] : undefined) ?? message;
}

/** Editor row — form-local shape of one `config.files` entry. `id` is only a
 *  React key; `headers` is the tag list for `expected_headers`. */
export interface SftpFileRow {
  id: string;
  type: SftpFileType;
  path: string;
  fileName: string;
  pattern: string;
  headers: string[];
}

/** Types that name one file (`file_name`) vs. folder types matched by `pattern`. */
export const SFTP_FILE_TYPE_META: Record<
  SftpFileType,
  { label: string; description: string; usesPattern: boolean }
> = {
  INPUT: {
    label: 'Input feed',
    description: 'The product feed we read from your server.',
    usesPattern: false,
  },
  OUTPUT: {
    label: 'Output (delivery)',
    description: 'Where enriched deliveries are written.',
    usesPattern: false,
  },
  IMAGES: { label: 'Images', description: 'Folder of product images.', usesPattern: true },
  TAXONOMY: { label: 'Taxonomy', description: 'Your taxonomy file.', usesPattern: false },
  CLIENT_VERIFICATION: {
    label: 'Client verification',
    description: 'Customer sign-off file.',
    usesPattern: false,
  },
};

let rowSeq = 0;
/** Fresh editor row with a unique React key. */
export function newSftpFileRow(type: SftpFileType = 'INPUT', path = ''): SftpFileRow {
  rowSeq += 1;
  return { id: `sftp-file-${String(rowSeq)}`, type, path, fileName: '', pattern: '', headers: [] };
}

function isSftpFileType(value: unknown): value is SftpFileType {
  return typeof value === 'string' && (SFTP_FILE_TYPES as readonly string[]).includes(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Read a stored `config.files` array into editor rows. Tolerates both casings
 * because the GET echo of `config` is stored snake_case but other readers of
 * connection config camelise keys.
 */
export function readSftpFileRows(files: unknown): SftpFileRow[] {
  if (!Array.isArray(files)) return [];
  const rows: SftpFileRow[] = [];
  for (const raw of files as unknown[]) {
    if (!raw || typeof raw !== 'object') continue;
    const entry = raw as Record<string, unknown>;
    if (!isSftpFileType(entry.type)) continue;
    const headers = entry.expected_headers ?? entry.expectedHeaders;
    rows.push({
      ...newSftpFileRow(entry.type, str(entry.path)),
      fileName: str(entry.file_name ?? entry.fileName),
      pattern: str(entry.pattern),
      headers: Array.isArray(headers)
        ? headers.filter((h): h is string => typeof h === 'string')
        : [],
    });
  }
  return rows;
}

/** Editor rows → stored snake_case entries (create / PATCH `config.files`). */
export function toSftpFileConfigs(rows: SftpFileRow[]): SftpFileConfig[] {
  return rows.map((row) => {
    const usesPattern = SFTP_FILE_TYPE_META[row.type].usesPattern;
    const fileName = row.fileName.trim();
    const pattern = row.pattern.trim();
    return {
      type: row.type,
      path: row.path.trim(),
      file_name: !usesPattern && fileName ? fileName : null,
      // Named-file types keep a stored pattern only when no name replaced it,
      // so an INPUT saved as a glob elsewhere survives a round-trip here.
      pattern: pattern && (usesPattern || !fileName) ? pattern : null,
      expected_headers: row.type === 'INPUT' && row.headers.length > 0 ? row.headers : null,
    };
  });
}

/** Editor rows → camelCase `files/validate` entries. */
export function toSftpFileWire(rows: SftpFileRow[]): SftpFileConfigWire[] {
  return toSftpFileConfigs(rows).map((f) => ({
    type: f.type,
    path: f.path,
    fileName: f.file_name ?? null,
    pattern: f.pattern ?? null,
    expectedHeaders: f.expected_headers ?? null,
  }));
}

/**
 * Placeholders assumed when the backend has no file-naming legend (404 — it
 * predates `{date}` / `{timestamp}` / `{run_id}`): `{subsidiary_code}` only.
 */
export const FALLBACK_FILE_NAME_PLACEHOLDERS: FileNamePlaceholder[] = [
  {
    name: 'subsidiary_code',
    token: SUBSIDIARY_CODE_PLACEHOLDER,
    description: "The subsidiary's short code (slug).",
    example: 'acme',
  },
];

/** Placeholders that change per delivery — a name with one of them creates a
 *  new file each day / second / run instead of overwriting the same file. */
const PER_DELIVERY_PLACEHOLDERS = new Set(['date', 'timestamp', 'run_id']);

/** Every `{…}` group in a name, as written (e.g. `{date}`, `{date:%Y}`). */
function placeholderGroups(name: string): string[] {
  return name.match(/\{[^{}]*\}/g) ?? [];
}

/** True when the name has a time/run placeholder (a new file per delivery). */
export function hasPerDeliveryPlaceholder(name: string): boolean {
  return placeholderGroups(name).some((group) => PER_DELIVERY_PLACEHOLDERS.has(group.slice(1, -1)));
}

/**
 * Instant client-side check of an OUTPUT `file_name`, mirroring the backend:
 * not empty, no path separators, braces balanced, and only the `allowed`
 * placeholder names (from the legend API, or the fallback). The debounced
 * server preview has the final word; this catches the obvious cases at once.
 * Returns an error message, or null when the name is acceptable.
 */
export function validateOutputFileName(
  name: string,
  allowed: readonly string[] = FALLBACK_FILE_NAME_PLACEHOLDERS.map((p) => p.name),
): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Output needs a file name to deliver to.';
  if (/[\\/]/.test(trimmed)) return 'File name cannot contain / or \\ — put folders in the path.';
  const allowedTokens = allowed.map((n) => `{${n}}`).join(', ');
  for (const group of placeholderGroups(trimmed)) {
    const inner = group.slice(1, -1);
    if (inner.includes(':'))
      return `Format specs like ${group} aren't supported. Use ${allowedTokens}.`;
    if (!allowed.includes(inner)) return `Unknown placeholder ${group}. Allowed: ${allowedTokens}.`;
  }
  // Anything left after removing well-formed groups is an unmatched brace.
  if (/[{}]/.test(trimmed.replace(/\{[^{}]*\}/g, ''))) {
    return `Unmatched brace. Placeholders look like ${allowedTokens}.`;
  }
  return null;
}

/**
 * Render an OUTPUT file name with the given placeholder values; any placeholder
 * without a value is left as written (e.g. `{run_id}` before the run exists).
 */
export function renderOutputFileName(name: string, values: Record<string, string>): string {
  return name.replace(/\{([^{}]*)\}/g, (group, inner: string) => values[inner] ?? group);
}

/**
 * The delivery target of an SFTP connection: the first OUTPUT entry with a
 * `file_name` — the same rule the backend's SFTP delivery handler applies.
 */
export function findSftpDeliveryTarget(
  config: Record<string, unknown> | undefined,
): { path: string; fileName: string } | null {
  const target = readSftpFileRows(config?.files).find(
    (row) => row.type === 'OUTPUT' && row.fileName.trim() !== '',
  );
  return target ? { path: target.path, fileName: target.fileName.trim() } : null;
}

/**
 * Client-side pre-submit check of editor rows, mirroring the backend's
 * `SFTPFileConfig` rules so the operator sees the problem on the row rather
 * than as a 422 toast. Returns messages keyed by row id (empty = valid).
 */
export function validateSftpFileRows(
  rows: SftpFileRow[],
  allowedPlaceholders?: readonly string[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const row of rows) {
    const usesPattern = SFTP_FILE_TYPE_META[row.type].usesPattern;
    if (!row.path.trim().startsWith('/')) {
      errors[row.id] = 'Enter an absolute folder path (starting with /).';
    } else if (row.type === 'OUTPUT') {
      const message = validateOutputFileName(row.fileName, allowedPlaceholders);
      if (message) errors[row.id] = message;
    } else if (usesPattern ? !row.pattern.trim() : !row.fileName.trim() && !row.pattern.trim()) {
      errors[row.id] = usesPattern ? 'Enter a file pattern, e.g. *.jpg.' : 'Enter a file name.';
    }
  }
  return errors;
}
